import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import AurAskLioButton from '../../aur-ask-lio/AurAskLioButton';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../redux/store';
import {setHasNotification} from '../../../redux/slices/askLioSlice';
import {Link as RouterLink, useLocation, useNavigate} from 'react-router-dom';
import {useModal} from '../../../contexts/modalContext/ModalContext';
import {resetChangeTracker} from '../../../redux/slices/globalChangeTrackerSlice';
import {setShowModal} from '../../../redux/slices/unSaveChangesModalSlice';
import AurTooltip from '../aur-tooltip/AurTooltip';
import {UrlPathEnums} from '../../../utils/enumUtils';
import {Box} from '@mui/material';

export type LeftNavItem = {
  name: string;
  icon: React.ReactNode;
  route: string;
  tooltip: string;
  // for making route active which are not same as current route
  activeRoutes?: string[];
  onClick?: (name: LeftNavItem) => void;
};

interface AurLeftNavProps {
  navItems: LeftNavItem[];
  isLioVisible: boolean;
  isLioOpen: boolean;
  setIsLioOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AurLeftNav: React.FC<AurLeftNavProps> = ({
  navItems,
  isLioVisible = true,
  isLioOpen = false,
  setIsLioOpen,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const changeState = useSelector(
    (state: RootState) => state.globalChangeTracker.changeState
  );
  const {showModal} = useModal();

  const drawerClasses = {
    paper: 'aur-leftnav-paper',
    root: 'aur-leftnav-root',
  };

  const listClasses = {
    root: 'aur-leftnav_list-root',
  };

  const listItemClasses = {
    root: 'aur-leftnav_listItem-root',
  };

  const listItemButtonClasses = {
    root: 'aur-leftnav_listItemButton-root',
  };

  const listItemIconClasses = {
    root: 'aur-leftnav_listItemIcon-root',
  };

  const tooltipClasses = {
    arrow: 'aur-leftnav_tooltip-arrow',
    tooltip: 'aur-leftnav_tooltip-tooltip typo-text label1_size',
  };

  const hasAskLioNotification = useSelector(
    (state: RootState) => state.askLioSlice.hasNotification
  );

  // Get the current location
  const location = useLocation();

  // Prevent sidebar navigation if there is any unsaved change
  const trackChange = (link: string) => {
    localStorage.setItem('currentLink', link);
    const path = localStorage.getItem('currentPath');
    if (
      path === UrlPathEnums.UsersBulkUploadResults ||
      path === UrlPathEnums.UsersAdd ||
      path === UrlPathEnums.UsersDetails ||
      path === UrlPathEnums.IndexSearch ||
      path === UrlPathEnums.NlpSearch ||
      path === UrlPathEnums.UploadFileCatalyst
    ) {
      dispatch(setShowModal(true));
      return;
    }
    localStorage.setItem('currentPath', '');
    dispatch(setShowModal(false));
    if (changeState.hasChange) {
      showModal({
        title: changeState.modalTitle,
        description: changeState.modalDescription,
        onOk: () => {
          navigate(link, {state: {source: 'nav-panel'}});
          dispatch(resetChangeTracker());
        },
        labelOkButton: changeState.okButtonLabel,
        labelCancelButton: changeState.closeButtonLabel,
        className: changeState.className,
      });
    } else {
      navigate(link, {state: {source: 'nav-panel'}});
    }
  };

  return (
    <Drawer variant="permanent" open={true} classes={drawerClasses}>
      <List classes={listClasses}>
        {navItems.map((navItem) => (
          <ListItem
            key={navItem.name}
            classes={listItemClasses}
            className={
              location.pathname === navItem.route ||
              (navItem.activeRoutes &&
                navItem.activeRoutes.indexOf(location.pathname) > -1)
                ? 'active'
                : ''
            }
            onClick={() => navItem?.onClick?.(navItem)}
          >
            <div className="aur-leftNav_listItem-border">
              <AurTooltip
                title={navItem.tooltip}
                PointerPlacement="right"
                disableInteractive
                classes={tooltipClasses}
                slotProps={{
                  popper: {
                    modifiers: [{name: 'offset', options: {offset: [0, 10]}}],
                  },
                }}
              >
                <ListItemButton
                  component={RouterLink}
                  to={navItem.route}
                  onClick={(e) => {
                    e.preventDefault();
                    trackChange(navItem.route);
                  }}
                  classes={listItemButtonClasses}
                >
                  <ListItemIcon classes={listItemIconClasses}>
                    {navItem.icon}
                  </ListItemIcon>
                </ListItemButton>
              </AurTooltip>
            </div>
          </ListItem>
        ))}
      </List>

      <Box>
        {isLioVisible && (
          <AurAskLioButton
            position="fixed"
            className="mt-32"
            variant={
              hasAskLioNotification
                ? 'notification'
                : isLioOpen
                  ? 'active'
                  : 'resting'
            }
            onClick={() => {
              setIsLioOpen(!isLioOpen);
              dispatch(setHasNotification(false));
            }}
          />
        )}
      </Box>
    </Drawer>
  );
};

export default AurLeftNav;
